import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Chip,
  IconButton,
  Grid,
  Autocomplete,
  Alert,
  AlertTitle,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
// Removed date picker imports
import axios from 'axios';
import { format } from 'date-fns';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    isActive: true,
    startDate: new Date(),
    endDate: null,
    minPurchase: 0,
    usageLimit: null,
    description: '',
    products: [],
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Fetch coupons and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch coupons
        const couponsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/coupons`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        // Fetch products for selection
        const productsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/products`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        setCoupons(couponsResponse.data.data?.data || []);
        
        // Extract products from the response based on structure from Products.js
        if (productsResponse.data?.status === 'success' && productsResponse.data.data?.data && Array.isArray(productsResponse.data.data.data)) {
          console.log('Products found:', productsResponse.data.data.data.length);
          setProducts(productsResponse.data.data.data);
        } else {
          console.error('Unexpected products data structure:', productsResponse.data);
          setProducts([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleOpen = (coupon = null) => {
    if (coupon) {
      // Edit mode
      setSelectedCoupon(coupon);
      setFormData({
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        isActive: coupon.isActive,
        startDate: coupon.startDate ? new Date(coupon.startDate) : new Date(),
        endDate: coupon.endDate ? new Date(coupon.endDate) : null,
        minPurchase: coupon.minPurchase || 0,
        usageLimit: coupon.usageLimit,
        description: coupon.description || '',
        products: coupon.products || [],
      });
      
      // Set selected products
      if (coupon.products && Array.isArray(coupon.products) && coupon.products.length > 0 && Array.isArray(products)) {
        const selectedProductObjs = products.filter(product => 
          product && product._id && coupon.products.includes(product._id)
        );
        setSelectedProducts(selectedProductObjs);
      } else {
        setSelectedProducts([]);
      }
    } else {
      // Create mode
      setSelectedCoupon(null);
      setFormData({
        code: '',
        type: 'percentage',
        value: '',
        isActive: true,
        startDate: new Date(),
        endDate: null,
        minPurchase: 0,
        usageLimit: null,
        description: '',
        products: [],
      });
      setSelectedProducts([]);
    }
    
    setOpen(true);
    setFormErrors({});
  };
  
  const handleClose = () => {
    setOpen(false);
    setSelectedCoupon(null);
    setFormErrors({});
  };
  
  const handleDeleteDialogOpen = (coupon) => {
    setSelectedCoupon(coupon);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedCoupon(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for the field
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };
  
  const handleSwitchChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };
  
  const handleProductsChange = (_, values) => {
    // Ensure values is an array
    const productArray = Array.isArray(values) ? values : [];
    setSelectedProducts(productArray);
    
    // Safely extract product IDs
    const productIds = productArray
      .filter(product => product && product._id) // Filter out any invalid products
      .map(product => product._id);
      
    setFormData({
      ...formData,
      products: productIds,
    });
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.code.trim()) {
      errors.code = 'Coupon code is required';
    }
    
    if (formData.type === 'percentage') {
      if (!formData.value || isNaN(formData.value)) {
        errors.value = 'Percentage value is required';
      } else if (formData.value < 0 || formData.value > 100) {
        errors.value = 'Percentage must be between 0 and 100';
      }
    } else {
      if (!formData.value || isNaN(formData.value)) {
        errors.value = 'Fixed amount is required';
      } else if (formData.value < 0) {
        errors.value = 'Amount must be greater than 0';
      }
    }
    
    if (formData.minPurchase < 0) {
      errors.minPurchase = 'Minimum purchase must be greater than or equal to 0';
    }
    
    if (formData.usageLimit !== null && formData.usageLimit < 1) {
      errors.usageLimit = 'Usage limit must be at least 1';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async () => {
    try {
      if (!validateForm()) {
        return;
      }
      
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Format dates properly for API
      const submitData = {
        ...formData
      };
      
      if (selectedCoupon) {
        // Update existing coupon
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/coupons/${selectedCoupon._id}`,
          submitData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        setSuccess('Coupon updated successfully');
      } else {
        // Create new coupon
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/coupons`,
          submitData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        setSuccess('Coupon created successfully');
      }
      
      // Refresh coupon list
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/coupons`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setCoupons(response.data.data.data || []);
      handleClose();
    } catch (error) {
      console.error('Error saving coupon:', error);
      setError(error.response?.data?.message || 'Failed to save coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    try {
      if (!selectedCoupon) return;
      
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/coupons/${selectedCoupon._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Refresh coupon list
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/coupons`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setCoupons(response.data.data.data || []);
      setSuccess('Coupon deleted successfully');
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      setError(error.response?.data?.message || 'Failed to delete coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'No End Date';
    return format(new Date(dateString), 'MM/dd/yyyy');
  };
  
  // Clears alerts after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error, success]);
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Coupon Management</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => handleOpen()}
            disabled={loading}
          >
            Create New Coupon
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Success</AlertTitle>
            {success}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Start Date</strong></TableCell>
                  <TableCell><strong>End Date</strong></TableCell>
                  <TableCell><strong>Products</strong></TableCell>
                  <TableCell><strong>Usage</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No coupons found. Create your first coupon!
                    </TableCell>
                  </TableRow>
                ) : (
                  coupons.map((coupon) => (
                    <TableRow key={coupon._id}>
                      <TableCell>{coupon.code}</TableCell>
                      <TableCell>
                        {coupon.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      </TableCell>
                      <TableCell>
                        {coupon.type === 'percentage' ? `${coupon.value}%` : `$${coupon.value.toFixed(2)}`}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={coupon.isActive ? 'Active' : 'Inactive'} 
                          color={coupon.isActive ? 'success' : 'error'} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(coupon.startDate)}</TableCell>
                      <TableCell>{formatDate(coupon.endDate)}</TableCell>
                      <TableCell>
                        {coupon.products && coupon.products.length > 0 
                          ? `${coupon.products.length} products` 
                          : 'All products'}
                      </TableCell>
                      <TableCell>
                        {coupon.usedCount || 0} {coupon.usageLimit ? `/ ${coupon.usageLimit}` : ''}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          color="primary" 
                          size="small" 
                          onClick={() => handleOpen(coupon)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          size="small" 
                          onClick={() => handleDeleteDialogOpen(coupon)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      
      {/* Add/Edit Coupon Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCoupon ? 'Edit Coupon' : 'Create New Coupon'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="code"
                label="Coupon Code"
                value={formData.code}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!formErrors.code}
                helperText={formErrors.code}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="value"
                label={formData.type === 'percentage' ? 'Percentage Off' : 'Amount Off'}
                value={formData.value}
                onChange={handleInputChange}
                fullWidth
                required
                type="number"
                error={!!formErrors.value}
                helperText={formErrors.value}
                InputProps={{
                  startAdornment: formData.type === 'fixed' ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : null,
                  endAdornment: formData.type === 'percentage' ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="minPurchase"
                label="Minimum Purchase Amount"
                value={formData.minPurchase}
                onChange={handleInputChange}
                fullWidth
                type="number"
                error={!!formErrors.minPurchase}
                helperText={formErrors.minPurchase}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="startDate"
                label="Start Date"
                type="date"
                value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="endDate"
                label="End Date (Optional)"
                type="date"
                value={formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value ? new Date(e.target.value) : null })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="usageLimit"
                label="Usage Limit (Optional)"
                value={formData.usageLimit === null ? '' : formData.usageLimit}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  setFormData({ ...formData, usageLimit: value });
                }}
                fullWidth
                type="number"
                error={!!formErrors.usageLimit}
                helperText={formErrors.usageLimit || 'Leave empty for unlimited use'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    name="isActive"
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              {Array.isArray(products) ? (
                <Autocomplete
                  multiple
                  options={products}
                  getOptionLabel={(option) => option?.name || ""}
                  value={selectedProducts}
                  onChange={handleProductsChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Apply to Specific Products (Optional)"
                      helperText="Leave empty to apply to all products"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option?._id || index}
                        label={option?.name || "Product"}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  noOptionsText="No products available"
                />
              ) : (
                <TextField
                  fullWidth
                  label="Apply to Specific Products"
                  disabled
                  helperText="Error loading products. Please refresh the page."
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description (Optional)"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : selectedCoupon ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the coupon <strong>{selectedCoupon?.code}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Coupons;